import type { Dictionary } from '../types';

const en: Dictionary = {
  nav: {
    services: 'Services',
    cases: 'Success Stories',
    about: 'About',
    contact: 'Contact',
    blog: 'Blog',
    cta: 'Start for free →',
    changeLangEs: 'Switch language to Spanish',
    changeLangEn: 'Switch language to English',
  },
  hero: {
    badge: 'Your Digital Partner · Madrid',
    h1Part1: 'Your website, ',
    h1Accent: 'alive',
    h1Part2: 'in a matter of days.',
    subtitle: 'Get more customers with a website that actually works. Start without paying a dime: we design your site, make sure you show up on Google, and help you sell more.',
    ctaPrimary: 'Start for free →',
    ctaSecondary: 'See results',
    pillars: [
      { key: 'Zero risk', value: 'Pay only if you love it' },
      { key: 'Show up on Google', value: 'Be found when they search' },
      { key: 'Ready in 72h', value: 'Don’t wait for months' },
    ],
  },
  manifiesto: {
    heading: 'We believe ',
    headingAccent: 'technology',
    left: 'We were born in Madrid, working side by side with carpenters, wedding planners, clinics and engineering firms that could not afford to wait six weeks for a mockup — or pay 2,000 € before seeing results.',
    right: 'So we flipped the contract: you start without paying. We design fast, show early, iterate with you. If you love it, we talk money. If not, nobody lost anything.',
    rightAccent: "It's that simple.",
  },
  valor: {
    overline: 'How we help you',
    heading: 'A website that ',
    headingAccent: 'works for you.',
    cards: [
      { title: 'Trust at first click', body: 'Custom, mobile-adapted design that validates your authority and brand.' },
      { title: 'Image that attracts', body: 'Project professionalism and gain the visibility you need to attract your ideal customers.' },
      { title: 'Conquer Google', body: 'Make sure your clients find you on Google when they search for what you do.' },
    ],
  },
  proceso: {
    overline: 'How we work',
    heading: 'Four steps. ',
    headingAccent: 'No endless meetings.',
    steps: [
      { title: 'Contact', body: 'Tell us your idea in a short form. We reply in under 24 h.', time: 'Day 0' },
      { title: 'Proposal', body: 'We send you an initial mockup. We adjust it together until it fits.', time: 'Day 1–3' },
      { title: 'Build', body: 'We develop your website so you show up on Google and help you sell more.', time: 'Day 3–7' },
      { title: 'Launch', body: 'We go live. Only now we talk payment. If you are not thrilled, you pay nothing.', time: 'Day 7' },
    ],
  },
  planes: {
    overline: 'Plans',
    heading: 'Your business, ',
    headingAccent: 'on autopilot.',
    items: [
      { 
        name: 'Seed', 
        tag: 'Risk-free start', 
        sub: 'FREE / forever', 
        features: [
          'One Page Design',
          'WhatsApp Button',
          'SSL Security',
          'Hosting included',
          'No custom domain'
        ] 
      },
      { 
        name: 'Sprout', 
        tag: 'Recommended', 
        sub: '25€ + VAT / mo', 
        features: [
          'Multi-page Design',
          'Get found on Google',
          '.com/.es domain included',
          '5 professional emails',
          'Full legal compliance'
        ] 
      },
      { 
        name: 'Custom', 
        tag: 'Bespoke', 
        sub: 'Custom budget', 
        features: [
          'Software Engineering',
          'CRM/ERP integrations',
          'Corporate intranet',
          'Hybrid mobile app',
          'Advanced support'
        ] 
      },
    ],
  },
  testimonios: {
    overline: 'Testimonials',
    heading: 'What those who ',
    headingAccent: 'already trusted us say.',
    items: [
      { q: '"It was like they were in our heads knowing exactly what we needed"', who: 'Marta Panizo Plaza', role: 'Owner · Boda M&J' },
      { q: '"Speed, close and professional treatment throughout"', who: 'CRC Zafra', role: 'Medical Check-ups' },
      { q: '"They were super fast. Their services were explained super clearly"', who: 'Antolín', role: 'Construcciones Antolín' },
      { q: '"The personal approach, the empathy, the attention to detail... all perfect"', who: 'Jorge Vega', role: 'CAC Director · Valladares Ing.' },
    ],
  },
  faq: {
    overline: 'Frequently Asked Questions',
    heading: 'What people ',
    headingAccent: 'often ask us.',
    items: [
      { q: 'What does "the client does nothing" mean?', a: 'You give us the basic information of your business (services, photos, contact) and we take care of all the technicalities: design, writing so you are found on Google, email configuration, domain, and legal compliance.' },
      { q: 'Why 25€ per month?', a: 'We want to democratize digital presence. It is a clear fee, with no small print, that includes everything necessary for your business to compete in the digital world without heavy initial investments.' },
      { q: 'Can I cancel anytime?', a: 'Yes. We do not believe in abusive long-term contracts. If the service does not help you grow, you do not have to stay.' },
      { q: 'What is the process after I contact you?', a: 'We ask for your basic data, prepare a mockup in less than 72 hours, and if you like it, we activate the entire ecosystem (emails, domain, SSL) in one click.' },
    ],
  },
  contacto: {
    overline: 'Let\'s talk',
    heading: 'Tell us your ',
    headingAccent: 'idea',
    subtitle: 'We take care of everything for 25€ + VAT per month. No initial payments. No technical complications.',
    fields: {
      nombre: { label: 'Name *', placeholder: 'Your name' },
      email: { label: 'Email *', placeholder: 'you@company.com' },
      telefono: { label: 'Phone (optional)', placeholder: '+34 ...' },
      mensaje: { label: 'Tell us your idea *', placeholder: 'What you do, who you sell to, what you need...' },
      legal: 'I accept the <a href="/politica-privacidad" class="underline hover:text-[var(--color-accent)]">privacy policy</a> and the <a href="/terminos-condiciones" class="underline hover:text-[var(--color-accent)]">terms and conditions</a>.',
    },
    submit: 'Start now →',
    headingSecondary: 'We reply today.',
  },
  footer: {
    tagline: 'Professional web design for 25€/mo. No initial payment. We take care of everything.',
    columns: {
      nav: {
        title: 'Navigation',
        items: { inicio: 'Home', servicios: 'Services', casos: 'Success Stories', acerca: 'About', contacto: 'Contact', blog: 'Blog' },
      },
      legal: {
        title: 'Legal',
        items: { avisoLegal: 'Legal Notice', consentimiento: 'Client Consent', accesibilidad: 'Accessibility Statement', reembolso: 'Refund Policy', privacidad: 'Privacy Policy', terminos: 'Terms & Conditions', cookies: 'Cookie Policy' },
      },
      contacto: { title: 'Contact' },
    },
    copyright: '© 2026 · Codiaj Madrid',
    madeBy: 'Handcrafted · no advances',
  },
  cookies: {
    message: 'This site uses cookies to improve your experience.',
    linkLabel: 'Cookie Policy',
    accept: 'Accept',
    reject: 'Reject',
    configure: 'Configure',
    save: 'Save preferences',
    settings: 'Privacy settings',
    ariaLabel: 'Cookie notice',
    ariaAccept: 'Accept all cookies',
    ariaReject: 'Reject non-essential cookies',
    ariaConfigure: 'Configure cookie preferences',
    categories: {
      necessary: {
        title: 'Necessary',
        desc: 'Essential for the site to work correctly (security, session, language).',
      },
      analytics: {
        title: 'Analytics',
        desc: 'They allow us to measure traffic and see which parts of the web we can improve.',
      },
      marketing: {
        title: 'Marketing',
        desc: 'Used to show you relevant advertising based on your interests.',
      },
    },
  },
  metadata: {
    home: { title: 'Professional Web Design for 25€/mo in Madrid | Codiaj', description: 'Web professional for SMEs and freelancers for 25€/mo. No initial payment. Get found on Google, emails and domain included. We take care of everything.' },
    servicios: { title: 'Hand-over Web Services for 25€/mo | CODIAJ', description: 'Simplify your business: web, SEO, emails and legal shield for a fixed fee of 25€/mo.' },
    acercaDe: { title: 'High-Impact Web Design and Digital Solutions in Madrid | CODIAJ', description: 'Meet CODIAJ, your digital partner and experts in solutions for SMEs in Madrid.' },
    contacto: { title: 'Contact | CODIAJ — Web Design Madrid', description: 'Start your web today for 25€/mo. No commitments, no initial payment. We respond in less than 24 hours.' },
    casosExito: { title: 'Success Cases | CODIAJ — Web Design Madrid', description: 'Real web design and digital solutions projects for SMEs and freelancers in Madrid.' },
    blog: { title: 'Blog | CODIAJ — Web Design Madrid', description: 'Articles on web design, SEO and digital marketing for SMEs and freelancers.' },
  },
  casos: {
    overline: 'Recent work',
    heading: 'projects. ',
    headingAccent: 'No upfront payments.',
    cta: 'View all →',
  },
  acercaDe: {
    hero: {
      overline: 'Who we are',
      h1Part1: 'Technology at the ',
      h1Accent: 'service',
      h1Part2: 'of your business.',
      subtitle: 'CODIAJ is a digital solutions team in Madrid that removes technical barriers. You give us the info, we do the magic for 25€/mo.',
      ctaPrimary: "Let's talk about your project →",
      ctaSecondary: 'See services',
    },
    pillars: {
      overline: 'How we work',
      heading: 'Three principles we ',
      headingAccent: "don't negotiate",
      items: [
        { title: 'No risk', body: 'No upfront payment. You pay only when you love the result. If you are not convinced, nothing happened — we bear the risk.' },
        { title: 'Real results', body: 'Websites oriented to attract clients, not just to look nice. Every design decision has a business goal behind it.' },
        { title: 'Close team', body: 'Direct contact, no middlemen, real people behind the work. You know who you are talking to and who signs off on your project from start to finish.' },
      ],
    },
    historia: {
      overline: 'Our story',
      heading: 'Born to simplify the complex.',
      p1: 'We saw too many freelancers frustrated with budgets of 3,000 € for a web that they then had to maintain alone. That\'s why we created the "Done-for-you" model.',
      p2: 'Today, for the price of a dinner, any SME in Spain can have the same technology as a large corporation.',
      p3: 'No outsourcing, no surprises. Just 25€ per month and a web that really works.',
    },
    stats: [
      { value: '25€', label: 'fixed monthly fee' },
      { value: '72h', label: 'first mockup' },
      { value: '100%', label: 'full management' },
      { value: 'Madrid', label: 'operations base' },
    ],
    cta: {
      heading: "Let's talk about ",
      headingAccent: 'your project',
      subtitle: 'No commitments. A mockup in 72 hours and you decide if we move forward.',
      button: 'Start project →',
    },
  },
  servicios: {
    hero: {
      title: 'Start for ',
      titleAccent: 'free',
      subtitle: 'Pay only when you are ready and 100% satisfied. No commitments.',
      billing: {
        monthly: 'Monthly',
        annual: 'Annual',
        save: 'Save 50€',
      },
    },
    plans: {
      semilla: {
        name: 'Seed',
        desc: 'The beginning. Ideal for those who need basic presence at no cost.',
        price: 'FREE',
        sub: 'Forever',
        features: [
          'Unified Experience Design (One Page)',
          'Instant Conversion Button (WhatsApp)',
          'SSL Security and Data Encryption Protocol',
          'Operational Web Deployment without Custom Domain'
        ],
        button: 'Start Now',
      },
      brota: {
        tag: 'Recommended',
        name: 'Sprout',
        desc: 'The boost your brand needs to stand out.',
        priceMonthly: '25€',
        priceAnnual: '250€',
        subMonthly: '+ VAT / month',
        subAnnual: '+ VAT / year',
        features: [
          'High-Impact and Conversion Web Design',
          'Web Optimized for Google Ranking (SEO)',
          'Corporate Digital Identity (.com / .es Domain)',
          'Instant Conversion Button (WhatsApp)',
          'Client Acquisition Form',
          'Professional Communication Suite (5 Email Accounts)',
          'Legal and Regulatory Shield (GDPR)',
          'SSL Security and Data Encryption Protocol'
        ],
        button: 'Choose Sprout Plan',
      },
      personalizado: {
        name: 'Custom',
        desc: 'Projects with specific needs and large scale such as E-Commerce.',
        price: 'Consult',
        sub: 'Budget',
        quote: '"Your vision, our technology: we create the platform that solves your challenges."',
        button: 'Contact the Team',
      },
    },
    extras: {
      title: 'Complement your Sprout Plan',
      subtitle: 'Add extra features to your base plan according to your needs',
      sections: [
        {
          title: 'Globalization and Markets',
          subtitle: 'Expand your reach to other languages and regions',
          items: [
            { name: 'AI Multi-language (1 Extra Language)', description: 'Smart translation of the entire web to a second language (e.g., Spanish)', price: '+10€ / mo' },
            { name: 'AI Multi-language (2 Extra Languages)', description: 'Smart translation to two additional languages (e.g., Spanish and French)', price: '+15€ / mo' },
          ]
        },
        {
          title: 'Data and Intelligence',
          subtitle: 'Measure and optimize your strategy with real data',
          items: [
            { name: 'Monthly Statistics', description: 'Detailed spreadsheet with performance, traffic, and conversion metrics', price: '+5€ / mo' },
          ]
        }
      ],
      table: {
        extra: 'Extra',
        description: 'Description',
        cost: 'Cost',
      },
      custom: {
        text: 'Need another feature? Tell us your case',
        button: 'Request Custom Extra',
      },
    },
    faq: {
      title: 'Still have doubts?',
      items: [
        { q: 'What does "the client does nothing" mean?', a: 'You give us the basic information of your business (services, photos, contact) and we take care of everything technical: design, SEO copywriting, email configuration, domain and legal compliance.' },
        { q: 'Why 25€ per month?', a: 'We want to democratize digital presence. It is a clear fee, with no small print, that includes everything necessary for your business to compete in the digital world without heavy initial investments.' },
        { q: 'Can I cancel anytime?', a: 'Yes. We do not believe in abusive long-term contracts. If the service does not help you grow, you do not have to stay.' },
      ],
      cta: 'Start now without commitment →',
    },
  },
  casosExito: {
    hero: {
      overline: 'Our work',
      h1Part1: 'Projects that speak ',
      h1Accent: 'for themselves',
      subtitle: 'Real projects. Each with a different problem, a specific solution and a measurable result.',
    },
    filters: {
      label: 'Filter by project type',
      todos: 'All',
      corporativa: 'Corporate',
      restaurante: 'Restaurant',
      artista: 'Artist',
      seguridad: 'Security',
    },
    empty: 'There are no projects in this category yet.',
    card: { view: 'View case →' },
    cta: {
      headingPart1: 'Is your project ',
      headingAccent: 'next',
      subtitle: 'Tell us what you need. Mockup in 72 hours, no upfront payments.',
      button: 'Start project →',
    },
  },
  casoDetail: {
    back: '← Back to cases',
    summary: 'Project summary',
    sidebar: {
      duration: '§ Duration',
      type: '§ Type',
      stack: '§ Stack',
      deliverables: '§ Deliverables',
    },
    nav: { prev: '← Previous', next: 'Next →' },
    cta: {
      headingPart1: "Let's talk about ",
      headingAccent: 'your project',
      subtitle: 'No upfront payment. Mockup in 72 hours. If you don\'t love it, you don\'t pay.',
      button: 'Start project →',
    },
  },
  blog: {
    hero: {
      overline: 'Thinking out loud',
      title: 'The ',
      titleAccent: 'notebook',
    },
    featured: {
      overline: 'Featured article',
      readMore: 'Read more →',
    },
    list: {
      overline: 'More articles',
      posts: 'entries',
      readMore: 'Read more →',
    },
    post: {
      back: '← The notebook',
      related: 'Other articles',
      readMore: 'Read →',
    },
    filters: {
      label: 'Filter by category',
      todos: 'All',
    },
    search: {
      placeholder: 'Search posts...',
    },
    pagination: {
      loadMore: 'Load more',
      noMore: 'No more posts',
    },
  },
  legal: {
    overline: 'Legal',
    toc: 'Index',
    updated: 'Last updated',
    questions: 'Questions? Write to us',
  },
};

export default en;
